// Notification Scheduler - Manages automatic notifications for calendar events, deadlines, and messages
import { PushNotificationService } from './pushNotifications';

export interface NotificationSettings {
  calendarEnabled: boolean;
  calendarReminders: ('15min' | '1hour' | '1day')[];
  deadlinesEnabled: boolean;
  deadlineReminders: ('24hours' | 'morning' | 'overdue')[];
  messagesEnabled: boolean;
  messagePreview: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string; // "22:00"
  quietHoursEnd: string; // "08:00"
  dailySummaryEnabled: boolean;
  dailySummaryTime: string; // "08:00"
}

interface ScheduledNotification {
  id: string;
  type: 'calendar' | 'deadline' | 'message' | 'summary';
  itemId: string;
  title: string;
  body: string;
  scheduledFor: Date;
  sent: boolean;
}

class NotificationScheduler {
  private settings: NotificationSettings;
  private scheduledNotifications: Map<string, ScheduledNotification>;
  private checkInterval: number | null = null;
  private notifiedItems: Set<string>; // Track what we've already notified about

  constructor() {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('whipt_notification_settings');
    this.settings = savedSettings ? JSON.parse(savedSettings) : this.getDefaultSettings();
    
    this.scheduledNotifications = new Map();
    this.notifiedItems = new Set();
    
    // Load notified items from localStorage
    const savedNotified = localStorage.getItem('whipt_notified_items');
    if (savedNotified) {
      this.notifiedItems = new Set(JSON.parse(savedNotified));
    }
  }

  private getDefaultSettings(): NotificationSettings {
    return {
      calendarEnabled: true,
      calendarReminders: ['15min', '1hour'],
      deadlinesEnabled: true,
      deadlineReminders: ['24hours', 'morning'],
      messagesEnabled: true,
      messagePreview: true,
      quietHoursEnabled: true,
      quietHoursStart: '22:00',
      quietHoursEnd: '08:00',
      dailySummaryEnabled: false,
      dailySummaryTime: '08:00'
    };
  }

  // Save settings to localStorage
  public updateSettings(newSettings: Partial<NotificationSettings>) {
    this.settings = { ...this.settings, ...newSettings };
    localStorage.setItem('whipt_notification_settings', JSON.stringify(this.settings));
    console.log('[NotificationScheduler] Settings updated:', this.settings);
  }

  public getSettings(): NotificationSettings {
    return { ...this.settings };
  }

  // Check if we're in quiet hours
  private isQuietHours(): boolean {
    if (!this.settings.quietHoursEnabled) return false;

    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    const start = this.settings.quietHoursStart;
    const end = this.settings.quietHoursEnd;

    // Handle overnight quiet hours (e.g., 22:00 - 08:00)
    if (start > end) {
      return currentTime >= start || currentTime < end;
    } else {
      return currentTime >= start && currentTime < end;
    }
  }

  // Schedule calendar event notifications
  public scheduleCalendarNotifications(events: any[]) {
    if (!this.settings.calendarEnabled) return;

    const now = new Date();

    events.forEach(event => {
      const eventDate = new Date(event.startDate || event.date);
      const eventId = event.id;

      // Skip past events
      if (eventDate < now) return;

      // Schedule notifications based on user preferences
      this.settings.calendarReminders.forEach(reminder => {
        let notifyTime: Date;
        let notificationId: string;

        switch (reminder) {
          case '15min':
            notifyTime = new Date(eventDate.getTime() - 15 * 60 * 1000);
            notificationId = `calendar-${eventId}-15min`;
            break;
          case '1hour':
            notifyTime = new Date(eventDate.getTime() - 60 * 60 * 1000);
            notificationId = `calendar-${eventId}-1hour`;
            break;
          case '1day':
            notifyTime = new Date(eventDate.getTime() - 24 * 60 * 60 * 1000);
            notificationId = `calendar-${eventId}-1day`;
            break;
          default:
            return;
        }

        // Only schedule if notify time is in the future and not already notified
        if (notifyTime > now && !this.notifiedItems.has(notificationId)) {
          const timeUntil = reminder === '15min' ? '15 minutes' : 
                           reminder === '1hour' ? '1 hour' : '1 day';

          this.scheduledNotifications.set(notificationId, {
            id: notificationId,
            type: 'calendar',
            itemId: eventId,
            title: `📅 Upcoming: ${event.title || 'Event'}`,
            body: `Starts in ${timeUntil}`,
            scheduledFor: notifyTime,
            sent: false
          });
        }
      });
    });

    console.log(`[NotificationScheduler] Scheduled ${this.scheduledNotifications.size} calendar notifications`);
  }

  // Schedule deadline/assignment notifications
  public scheduleDeadlineNotifications(items: any[]) {
    if (!this.settings.deadlinesEnabled) return;

    const now = new Date();

    items.forEach(item => {
      if (!item.dueDate || item.completed) return;

      const dueDate = new Date(item.dueDate);
      const itemId = item.id;

      // Skip past deadlines (unless overdue notification is enabled)
      const isOverdue = dueDate < now;

      this.settings.deadlineReminders.forEach(reminder => {
        let notifyTime: Date;
        let notificationId: string;
        let title: string;
        let body: string;

        switch (reminder) {
          case '24hours':
            notifyTime = new Date(dueDate.getTime() - 24 * 60 * 60 * 1000);
            notificationId = `deadline-${itemId}-24hours`;
            title = `⏰ Due Tomorrow`;
            body = `"${item.title || item.text}" due tomorrow at ${dueDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
            break;
          case 'morning':
            // Morning of due date (8 AM)
            notifyTime = new Date(dueDate);
            notifyTime.setHours(8, 0, 0, 0);
            notificationId = `deadline-${itemId}-morning`;
            title = `📝 Due Today`;
            body = `"${item.title || item.text}" due today at ${dueDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
            break;
          case 'overdue':
            if (!isOverdue) return;
            notifyTime = now;
            notificationId = `deadline-${itemId}-overdue`;
            title = `🚨 Overdue`;
            body = `"${item.title || item.text}" was due ${this.getRelativeTime(dueDate)}`;
            break;
          default:
            return;
        }

        // Only schedule if notify time is in the future and not already notified
        if (notifyTime > now && !this.notifiedItems.has(notificationId)) {
          this.scheduledNotifications.set(notificationId, {
            id: notificationId,
            type: 'deadline',
            itemId: itemId,
            title,
            body,
            scheduledFor: notifyTime,
            sent: false
          });
        }
      });
    });

    console.log(`[NotificationScheduler] Scheduled ${this.scheduledNotifications.size} deadline notifications`);
  }

  // Send message notification
  public async sendMessageNotification(message: any, senderName: string) {
    if (!this.settings.messagesEnabled || this.isQuietHours()) return;

    const notificationId = `message-${message.id}`;
    
    // Don't send duplicate message notifications
    if (this.notifiedItems.has(notificationId)) return;

    const title = `💬 Message from ${senderName}`;
    const body = this.settings.messagePreview 
      ? message.text?.substring(0, 100) || 'New message'
      : 'You have a new message';

    await PushNotificationService.showPersistentNotification(title, body, {
      tag: notificationId,
      data: { type: 'message', messageId: message.id }
    });

    this.notifiedItems.add(notificationId);
    this.saveNotifiedItems();
  }

  // Send daily summary
  public async sendDailySummary(upcomingItems: any[]) {
    if (!this.settings.dailySummaryEnabled || this.isQuietHours()) return;

    const today = new Date().toDateString();
    const notificationId = `summary-${today}`;

    // Only send once per day
    if (this.notifiedItems.has(notificationId)) return;

    const count = upcomingItems.length;
    const title = `☀️ Good Morning!`;
    const body = count > 0 
      ? `You have ${count} task${count === 1 ? '' : 's'} due today`
      : `No tasks due today. Have a great day!`;

    await PushNotificationService.showPersistentNotification(title, body, {
      tag: notificationId,
      data: { type: 'summary' }
    });

    this.notifiedItems.add(notificationId);
    this.saveNotifiedItems();
  }

  // Check and send due notifications
  private async checkNotifications() {
    if (this.isQuietHours()) {
      console.log('[NotificationScheduler] Quiet hours - skipping notifications');
      return;
    }

    const now = new Date();
    const toSend: ScheduledNotification[] = [];

    // Find notifications that are due
    this.scheduledNotifications.forEach((notification, id) => {
      if (!notification.sent && notification.scheduledFor <= now) {
        toSend.push(notification);
      }
    });

    // Send notifications
    for (const notification of toSend) {
      try {
        await PushNotificationService.showPersistentNotification(
          notification.title,
          notification.body,
          {
            tag: notification.id,
            data: { type: notification.type, itemId: notification.itemId }
          }
        );

        // Mark as sent
        notification.sent = true;
        this.notifiedItems.add(notification.id);
        this.scheduledNotifications.delete(notification.id);

        console.log(`[NotificationScheduler] Sent notification: ${notification.title}`);
      } catch (error) {
        console.error('[NotificationScheduler] Failed to send notification:', error);
      }
    }

    if (toSend.length > 0) {
      this.saveNotifiedItems();
    }
  }

  // Start the notification scheduler
  public start(checkIntervalMs: number = 60000) { // Check every minute
    if (this.checkInterval !== null) {
      console.log('[NotificationScheduler] Already running');
      return;
    }

    console.log('[NotificationScheduler] Starting scheduler (check every', checkIntervalMs / 1000, 'seconds)');
    
    // Check immediately
    this.checkNotifications();

    // Then check at intervals
    this.checkInterval = window.setInterval(() => {
      this.checkNotifications();
    }, checkIntervalMs);
  }

  // Stop the notification scheduler
  public stop() {
    if (this.checkInterval !== null) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('[NotificationScheduler] Stopped');
    }
  }

  // Clear all scheduled notifications
  public clearAll() {
    this.scheduledNotifications.clear();
    this.notifiedItems.clear();
    localStorage.removeItem('whipt_notified_items');
    console.log('[NotificationScheduler] Cleared all notifications');
  }

  // Reset notifications for a specific day (useful for testing)
  public resetToday() {
    const today = new Date().toDateString();
    const summaryId = `summary-${today}`;
    this.notifiedItems.delete(summaryId);
    this.saveNotifiedItems();
    console.log('[NotificationScheduler] Reset today\'s notifications');
  }

  private saveNotifiedItems() {
    localStorage.setItem('whipt_notified_items', JSON.stringify([...this.notifiedItems]));
  }

  private getRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    return 'just now';
  }

  // Get status for debugging
  public getStatus() {
    return {
      settings: this.settings,
      scheduled: this.scheduledNotifications.size,
      notified: this.notifiedItems.size,
      running: this.checkInterval !== null,
      quietHours: this.isQuietHours()
    };
  }
}

// Export singleton instance
export const notificationScheduler = new NotificationScheduler();
